import { useState, useMemo, PropsWithChildren } from 'react'
import styles from './ProducerSliderLayout.module.scss'
import { ProducerSliderLayoutProps } from 'components/producer-slider-layout/types'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { getCapacityState, getEnergyNameI18Key, getProducerLocation } from 'utils/producers'
import PriceDisplay from 'features/simulation/components/proposition-card/price-display/PriceDisplay'
import { getProposition } from 'utils/price'
import classNames from 'classnames'
import FixedPriceDrawer from 'features/registration/components/registration-side-card/sales-prices-section/FixedPriceDrawer'
import { Button, Drawer, Icon, Img } from '@boltenergy-be/design-system'
import { Language } from 'types/language'
import { SimulationFieldKeys } from 'store/customer/enums'
import { useStoreSelector } from 'hooks/store'
import { CldImage } from 'next-cloudinary'
import { EnergyType } from 'types/producer'
import { SIMULATION_TYPES } from '@boltenergy-be/shared'

const ProducerSliderLayout = ({ producer, withPrice, children }: PropsWithChildren<ProducerSliderLayoutProps>) => {
  // Redux store
  const { fixedPrices, inputs } = useStoreSelector((store) => store.customer)
  const { deliveryAddress, simulation: simulationInputs } = useStoreSelector((store) => store.customer.inputs)
  const { simulation } = useStoreSelector((store) => store.customer.flows)
  const { confirmationFromPortalRegistration } = useStoreSelector((store) => store.marketing)

  // Router
  const { locale } = useRouter()

  // i18n
  const { t } = useTranslation(['simulationCard', 'common', 'producers'])

  // Local state
  const [showMobileDrawer, setShowMobileDrawer] = useState<boolean>(false)
  const [showSalePriceMobileModal, setShowSalePriceMobileModal] = useState<boolean>(false)

  // Constants
  const proposition = getProposition()
  const capacityState = useMemo(() => getCapacityState(producer, inputs.simulation, true), [inputs.simulation, producer])

  return (
    <>
      <div
        className={classNames(styles.card, { [styles['has-price']]: withPrice })}
        role="presentation"
        onClick={() => setShowMobileDrawer(true)}
      >
        <div className={styles['producer-container']}>
          <picture>
            <CldImage className={styles.thumbnail} alt={producer.name[locale]} src={producer.images.profile?.url} fill />
            <div className={styles['energy-types']}>
              {deliveryAddress.needsGas && <Icon name="gas" className={styles.gas} size={16} />}
              <Icon name="electricity" className={styles.electricity} size={16} />
            </div>
          </picture>

          <div className={styles.producer}>
            <small>
              {t(`energyTypeAndLocation.${producer.energyType === EnergyType.BIO ? 'bio' : 'other'}`, {
                energyType:
                  locale === Language.FRENCH
                    ? t(getEnergyNameI18Key(producer.energyType)).toLowerCase()
                    : t(getEnergyNameI18Key(producer.energyType)),
                location: getProducerLocation(locale, producer?.location)
              })}
            </small>
            <h3>{producer.name[locale]}</h3>
          </div>

          <span className={classNames(styles['open-icon'], { [styles.warning]: capacityState.soldOut })}>
            {capacityState.soldOut ? <Icon name="exclamation" size={20} className={styles.warning} /> : <Icon name="chevronUp" size={20} />}
          </span>
        </div>

        {withPrice && inputs.simulation.chosenSimulationType !== SIMULATION_TYPES.eav && (
          <>
            <hr />
            <div className={styles['price-container']}>
              {!confirmationFromPortalRegistration && inputs.simulation.chosenSimulationType === SIMULATION_TYPES.fixedAmount ? (
                <Button
                  variant="layered"
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowSalePriceMobileModal(true)
                  }}
                >
                  {t('common:change', 'Wijzig')}
                </Button>
              ) : (
                <span className={styles.product}>{t(`common:${proposition}`, 'Bolt')}</span>
              )}
              <PriceDisplay
                forcedAmount={simulationInputs[SimulationFieldKeys.FORCED_AMOUNT]}
                proposition={inputs.simulation.chosenSimulationType !== SIMULATION_TYPES.fixedAmount && proposition}
                fixedPrices={inputs.simulation.chosenSimulationType === SIMULATION_TYPES.fixedAmount && fixedPrices}
                label={t('perMonth', '/maand')}
                className={styles.price}
                isLoading={simulation.isLoading}
              />
            </div>
          </>
        )}
      </div>

      <Drawer
        isOpen={showMobileDrawer}
        onRequestClose={() => setShowMobileDrawer(false)}
        customIcon="chevronDown"
        className={styles['producer-drawer']}
      >
        <Img //Need to substring the publicId from the url to get the right image
          publicId={producer.images.profile.url.substring(
            producer.images.profile.url.indexOf('/producer'),
            producer.images.profile.url.length
          )}
          alt={producer.name[locale]}
          fill
        />
        {children}
      </Drawer>

      <FixedPriceDrawer onRequesteClose={() => setShowSalePriceMobileModal(false)} isOpen={showSalePriceMobileModal} />
    </>
  )
}

export default ProducerSliderLayout
